/**
 * 权限检查工具函数
 * 用于检查用户的角色权限、全局配置和群组权限
 */

import useUserStore from '../store/userStore.js';
import useGroupStore from '../store/groupStore.js';
import useConfigStore from '../store/configStore.js';

/**
 * 检查角色权限
 * @param {string} permKey - 权限键,支持点号分隔,如 'message.forward'
 * @returns {boolean} 是否拥有该权限
 */
export function hasRolePermission(permKey) {
	const userStore = useUserStore();

	// 权限未加载时默认返回 false,避免误展示敏感功能
	if (!userStore.rolePermissionsLoaded) {
		return false;
	}

	// 没有角色权限配置时返回 false
	if (!userStore.rolePermissions) {
		return false;
	}

	const normalizeBoolean = (value) => {
		if (value === true) {
			return true;
		}
		if (value === false) {
			return false;
		}
		if (typeof value === 'number') {
			return value !== 0;
		}
		if (typeof value === 'string') {
			return value === 'true' || value === '1';
		}
		return false;
	};

	// 支持点号分隔的权限键,如 'message.forward' -> rolePermissions.message.forward
	const keys = permKey.split('.');
	let value = userStore.rolePermissions;

	for (const key of keys) {
		if (value && typeof value === 'object' && key in value) {
			value = value[key];
		} else {
			return false;
		}
	}

	// 最终值应可转为 boolean
	return normalizeBoolean(value);
}

/**
 * 获取全局配置
 * @param {string} configKey - 配置键,支持点号分隔,如 'group.maxMemberCount'
 * @returns {any} 配置值
 */
export function getGlobalConfig(configKey) {
	const configStore = useConfigStore();

	if (!configStore.globalConfig) {
		return undefined;
	}

	// 支持点号分隔的配置键
	const keys = configKey.split('.');
	let value = configStore.globalConfig;

	for (const key of keys) {
		if (value && typeof value === 'object' && key in value) {
			value = value[key];
		} else {
			return undefined;
		}
	}

	return value;
}

/**
 * 检查群组权限
 * @param {number} groupId - 群组ID
 * @param {string} permKey - 权限键,如 'isAllowViewMemberProfile'
 * @returns {boolean} 是否拥有该权限
 */
export function hasGroupPermission(groupId, permKey) {
	const groupStore = useGroupStore();
	const userStore = useUserStore();
	const group = groupStore.findGroup(groupId);

	// 群组信息未加载时默认返回 false
	if (!group) {
		return false;
	}

	// 群主/管理员不受群组权限限制
	if (group.ownerId === userStore.userInfo.id || groupStore.isManager(groupId, userStore.userInfo.id)) {
		return true;
	}

	// 使用群组权限配置,默认 true(与数据库默认值一致)
	return group[permKey] !== false;
}

/**
 * 检查撤回权限(考虑时间限制)
 * @param {object} message - 消息对象
 * @param {number} groupId - 群组ID(可选,私聊时为null)
 * @returns {boolean} 是否可以撤回该消息
 */
export function canRecallMessage(message, groupId = null) {
	const userStore = useUserStore();
	const groupStore = useGroupStore();

	// 只能撤回自己的消息
	if (message.sendId !== userStore.userInfo.id) {
		return false;
	}

	// 群主/管理员不受限制
	if (groupId) {
		const group = groupStore.findGroup(groupId);
		if (group && (group.ownerId === userStore.userInfo.id || groupStore.isManager(groupId, userStore.userInfo.id))) {
			return true;
		}
	}

	// 检查撤回时间限制(单位:分钟)
	const recallTimeLimit = getRecallTimeLimit();

	if (recallTimeLimit === 0) {
		return false; // 不能撤回
	}

	const messageTime = new Date(message.sendTime).getTime();
	const now = Date.now();
	const limitMs = recallTimeLimit * 60 * 1000;

	return (now - messageTime) <= limitMs;
}

/**
 * 获取撤回时间限制
 * @returns {number} 撤回时间限制(分钟)
 */
export function getRecallTimeLimit() {
	const userStore = useUserStore();

	if (!userStore.rolePermissions) {
		return 2; // 默认2分钟
	}

	if (!userStore.rolePermissions.message) {
		return 2; // 默认2分钟
	}

	return userStore.rolePermissions.message.recallTimeLimit ?? 2;
}

/**
 * 获取会话置顶数量限制
 * @returns {number} 会话置顶数量限制
 */
export function getSessionTopLimit() {
	const configStore = useConfigStore();

	if (!configStore.globalConfig || !configStore.globalConfig.session) {
		return 10; // 默认10个
	}

	return configStore.globalConfig.session.topLimit ?? 10;
}

/**
 * 获取群消息置顶数量限制
 * @returns {number} 群消息置顶数量限制
 */
export function getGroupMessageTopLimit() {
	const configStore = useConfigStore();

	if (!configStore.globalConfig || !configStore.globalConfig.group) {
		return 0;
	}

	return configStore.globalConfig.group.messageTopLimit ?? 0;
}

/**
 * 获取群组最大成员数量限制
 * @returns {number} 群组最大成员数
 */
export function getMaxMemberCount() {
	const configStore = useConfigStore();

	if (!configStore.globalConfig || !configStore.globalConfig.group) {
		return 50000; // 默认50000人
	}

	return configStore.globalConfig.group.maxMemberCount ?? 50000;
}

/**
 * 检查消息编辑权限
 * @param {object} message - 消息对象
 * @returns {boolean} 是否可以编辑该消息
 */
export function canEditMessage(message) {
	const userStore = useUserStore();

	// 只能编辑自己的消息
	if (message.sendId !== userStore.userInfo.id) {
		return false;
	}

	// 需要 message.silentEdit 权限
	return hasRolePermission('message.silentEdit');
}

/**
 * 检查是否可以控制在线状态可见性
 * @returns {boolean} 是否可以控制
 */
export function canControlOnlineVisibility() {
	// 需要角色权限 online.control=true 才能控制
	return hasRolePermission('online.control');
}

/**
 * 检查是否可以创建群组
 * @returns {boolean} 是否可以创建群组
 */
export function canCreateGroup() {
	return hasRolePermission('group.create');
}

/**
 * 检查是否可以转发消息
 * @returns {boolean} 是否可以转发
 */
export function canForwardMessage() {
	return hasRolePermission('message.forward');
}

/**
 * 检查是否可以转发或分享名片
 * @returns {boolean} 是否可以转发或分享名片
 */
export function canForwardCardMessage() {
	return hasRolePermission('message.cardForward');
}

/**
 * 检查是否可以多选消息
 * @returns {boolean} 是否可以多选
 */
export function canMultiSelectMessage() {
	return hasRolePermission('message.multiSelect');
}

export default {
	hasRolePermission,
	getGlobalConfig,
	hasGroupPermission,
	canRecallMessage,
	canEditMessage,
	canControlOnlineVisibility,
	canCreateGroup,
	canForwardMessage,
	canForwardCardMessage,
	canMultiSelectMessage,
	getSessionTopLimit,
	getGroupMessageTopLimit,
	getMaxMemberCount
};
